// @flow
import * as React from 'react';
import styled, { css, ThemeProvider } from 'styled-components';
import { ScrollContextProvider } from 'containers/ScrollContext';
import { ViewportContextProvider } from 'containers/ViewportContext';
import Box from 'components/Box';
import NoAmp from 'containers/NoAmp';
import ClientOnly from 'containers/ClientOnly';
import FlashMessageContainer from 'containers/FlashMessageContainer';
import AcceptCookiesContainer from 'containers/AcceptCookiesContainer';
import ModalRoot from 'containers/ModalRoot';
import FixedSpacerContainer from 'containers/FixedSpacerContainer';
import PrintHidden from 'components/PrintHidden';
import LanguageLinks from 'components/LanguageLinks';
import { isBrowser } from 'utils/browser';
import { getItem } from 'utils/localStorage';
import { isDefined } from 'utils/functional';
import theme, {
  utilityClasses,
  fonts,
  fontWeights,
  fontSizesByType,
  breakpoints,
  colors,
  gutterWidth,
  maxWidth,
  textStyles
} from '../theme';

const testMode = getItem('testMode');

const StyledLanguageLinks = styled(LanguageLinks)`
  a {
    position: relative;
    margin: 0 16px;
    color: ${colors.battleshipGrey};
    ${textStyles.bodySmall};

    &:not(:first-child) {
      &::before {
        content: '';
        position: absolute;
        left: -16px;
        top: 0;
        width: 1px;
        height: 100%;
        background: ${colors.silver};
        pointer-events: none;
      }
    }
  }
`;

export const SimpleFooter = () => (
  <Box mt={72} mb={48} classes={['flex', 'justify-center']}>
    <StyledLanguageLinks
      showIcon={false}
      url={{
        en: isBrowser() ? window.location.pathname : '#', // @TODO - pull this out of Layout
        es: isBrowser() ? window.location.pathname : '#',
        pt: isBrowser() ? window.location.pathname : '#'
      }}
    />
  </Box>
);

const Root = styled(Box)`
  font-family: ${fonts.sans};
  color: ${colors.greyishBrown};
  * {
    box-sizing: border-box;
    font-family: ${fonts.sans};
  }
  svg {
    box-sizing: content-box;
    pointer-events: none;
  }
  strong {
    font-weight: ${fontWeights.semiBold};
  }
  @page {
    margin-bottom: 0;
    size: auto;
    box-decoration-break: clone;
  }
  ${utilityClasses.map(
    c =>
      css`
        .idlst-${c.name.replace(/[aeiou-]/g, '')} {
          ${c.styles}
        }
      `
  )}
`;

const PageContent = styled(Box)`
  ${props =>
    props.$centered &&
    `
    width: 100%;
    max-width: ${maxWidth[2]}px;
    padding: 0 ${gutterWidth / 2}px;
    margin: 0 auto;
    @media (min-width: ${breakpoints[2]}) {
      padding: 0 ${gutterWidth}px;
    }
  `}
`;

const buttonBarHeight = '67px';

const ButtonBarContainer = styled.div`
  display: none;
  height: ${buttonBarHeight};
  @media (max-width: ${breakpoints[2]}) {
    display: block;
  }
`;

const ButtonBar = styled(Box)`
  margin-bottom: 0;
  position: fixed;
  height: ${buttonBarHeight};
  width: 100%;
  left: 0;
  bottom: 0;
  background: white;
  box-shadow: 0 -3px 4px 1px rgba(0, 0, 0, 0.15);
  & > button,
  & > a {
    flex-grow: 1;
    &:first-child {
      margin-left: 16px;
    }
    margin-right: 16px;
  }
`;

const BaseLayout = ({
  children,
  header,
  buttonBar,
  centered,
  showSimpleFooter,
  stickyHeaderHeight
}: {
  children: React.Node,
  header?: React.Node,
  buttonBar?: React.Node,
  centered?: boolean,
  showSimpleFooter?: boolean,
  stickyHeaderHeight?: ?number | Array<?number>
}) => (
  <ThemeProvider theme={theme}>
    <ScrollContextProvider>
      <ViewportContextProvider>
        <Root
          bg={centered && colors.fog}
          fontSize={fontSizesByType.body}
          id="layout-root"
        >
          <Box classes={['flex']} minHeight="100vh" flexDirection="column">
            {header}
            <ClientOnly>
              <Box
                position={
                  testMode || !isDefined(stickyHeaderHeight)
                    ? 'static'
                    : typeof stickyHeaderHeight === 'number'
                    ? 'sticky'
                    : stickyHeaderHeight.map(height =>
                        isDefined(height) ? 'sticky' : 'static'
                      )
                }
                top={stickyHeaderHeight}
                zIndex={3}
              >
                <FlashMessageContainer />
              </Box>
            </ClientOnly>
            <PageContent
              classes={['flex']}
              flexDirection="column"
              flex="1 1 auto"
              $centered={centered}
            >
              {children}
            </PageContent>
            {showSimpleFooter && <SimpleFooter />}
          </Box>

          {buttonBar && (
            <NoAmp>
              <PrintHidden>
                <ButtonBarContainer>
                  <ButtonBar
                    classes={['flex', 'justify-center', 'align-center']}
                  >
                    {buttonBar}
                  </ButtonBar>
                </ButtonBarContainer>
              </PrintHidden>
            </NoAmp>
          )}

          <FixedSpacerContainer
            bottom={0}
            left={0}
            zIndex={80}
            transform="translateZ(0)"
            width="100%"
          >
            <ClientOnly>
              <AcceptCookiesContainer />
            </ClientOnly>
          </FixedSpacerContainer>
          <ModalRoot />
        </Root>
      </ViewportContextProvider>
    </ScrollContextProvider>
  </ThemeProvider>
);

export default BaseLayout;
